/**
 * Submenu panels interaction.
 *
 * Matches header menu items against fullscreen submenu panels by their
 * normalized title (panel[data-submenu-parent]).
 *
 * Desktop (>= 990px): hover / keyboard focus on the menu item opens the
 * matching fullscreen panel; moving out (with a grace delay), pressing Escape,
 * clicking outside or focusing elsewhere closes it.
 *
 * Mobile drawer: a caret trigger is injected next to matching top-level
 * drawer links; tapping it moves the panel into the menu item and expands it
 * (tap again to collapse and move the panel back into the shared container).
 */
const DESKTOP_QUERY = '(min-width: 990px)';
const HOVER_GRACE_MS = 180;

class SubmenuPanels {
  constructor() {
    this.container = document.querySelector('[data-submenu-panels]');
    if (!this.container) return;

    /* Shopify wraps rendered blocks in .shopify-block elements, so panels are
       matched by class at any depth rather than as direct children. */
    this.panels = Array.from(this.container.querySelectorAll('.submenu-panel'));
    if (!this.panels.length) return;

    this.normalize = (s) => (s || '').trim().toLowerCase();
    this.panelByKey = new Map();
    this.panels.forEach((panel) => {
      const key = this.normalize(panel.dataset.submenuParent);
      if (key) this.panelByKey.set(key, panel);
    });

    this.activePanel = null;
    this.activeTrigger = null;
    this.closeTimer = null;
    this.mq = window.matchMedia(DESKTOP_QUERY);

    this.initDesktop();
    this.initDrawer();
    this.initGlobalListeners();
  }

  panelHasContent(panel) {
    return !!(
      panel.querySelector('.submenu-panel__menu') ||
      panel.querySelector('.submenu-panel__promo')
    );
  }

  /* ---------- Desktop ---------- */

  initDesktop() {
    const items = document.querySelectorAll(
      'header-menu .menu-list__list-item'
    );

    items.forEach((li) => {
      const titleEl = li.querySelector('.menu-list__link-title');
      if (!titleEl) return;
      const panel = this.panelByKey.get(this.normalize(titleEl.textContent));
      if (!panel) return;

      const link = li.querySelector('a.menu-list__link');
      if (!link) return;

      link.setAttribute('aria-expanded', 'false');
      link.setAttribute('aria-controls', panel.id);

      li.addEventListener('pointerenter', () => {
        if (!this.mq.matches) return;
        this.open(panel, link);
      });
      li.addEventListener('pointerleave', () => {
        if (!this.mq.matches) return;
        this.scheduleClose();
      });
      link.addEventListener('focus', () => {
        if (!this.mq.matches) return;
        this.open(panel, link);
      });
    });
  }

  /* ---------- Mobile drawer ---------- */

  initDrawer() {
    this.drawer =
      document.querySelector('.header__drawer') ||
      document.querySelector('[data-hydration-key="header-drawer-mobile"]');
    if (!this.drawer) return;

    /* Panels with content attach to matching top-level drawer entries (both
       <a> and <summary> carry --mainlist). A caret trigger is injected at the
       row's right edge only when the panel has content; tapping it moves the
       panel into the row and expands it. */
    const entries = this.drawer.querySelectorAll(
      '.menu-drawer__menu-item--mainlist'
    );

    entries.forEach((entry) => {
      const titleEl = entry.querySelector('.menu-drawer__menu-item-text');
      if (!titleEl) return;
      const panel = this.panelByKey.get(this.normalize(titleEl.textContent));
      if (!panel || !this.panelHasContent(panel)) return;

      const li = entry.closest('li');
      if (!li || li.querySelector('.submenu-panel__trigger')) return;

      li.classList.add('has-submenu-panel-trigger');

      const trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.className = 'submenu-panel__trigger button-unstyled';
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-label', `${titleEl.textContent} submenu`);
      trigger.innerHTML =
        '<svg class="submenu-panel__trigger-icon" aria-hidden="true" focusable="false" viewBox="0 0 14 14" fill="none"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" style="stroke-width: var(--icon-stroke-width, 1.5);"/></svg>';

      const togglePanel = () => {
        if (this.activePanel === panel) {
          this.close();
          return;
        }
        this.close();
        li.appendChild(panel);
        panel.classList.add('is-open', 'is-in-drawer');
        panel.removeAttribute('inert');
        trigger.setAttribute('aria-expanded', 'true');
        this.activePanel = panel;
        this.activeTrigger = trigger;
      };

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        togglePanel();
      });

      /* The whole row toggles the panel: tapping the link (or anywhere else
         in the row) expands/collapses instead of navigating. Clicks inside
         the opened panel (card links etc.) are left untouched. */
      li.addEventListener('click', (event) => {
        if (event.target.closest('.submenu-panel')) return;
        if (event.target.closest('.submenu-panel__trigger')) return;
        if (event.target.closest('a, summary')) event.preventDefault();
        event.stopPropagation();
        togglePanel();
      });

      li.appendChild(trigger);
    });
  }

  /* ---------- Shared ---------- */

  open(panel, trigger) {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (this.activePanel === panel) return;

    this.close();
    /* Dormant panel: no menu tree and no cards -> do not open */
    if (!this.panelHasContent(panel)) return;

    this.activePanel = panel;
    this.activeTrigger = trigger;
    panel.removeAttribute('inert');
    panel.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
  }

  close() {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }

    const panel = this.activePanel;
    if (!panel) return;

    if (this.activeTrigger) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
    }

    panel.classList.remove('is-open');
    if (panel.parentElement !== this.container) {
      panel.classList.remove('is-in-drawer');
      this.container.appendChild(panel);
    }
    panel.setAttribute('inert', '');

    this.activePanel = null;
    this.activeTrigger = null;
  }

  scheduleClose() {
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), HOVER_GRACE_MS);
  }

  initGlobalListeners() {
    this.panels.forEach((panel) => {
      panel.addEventListener('pointerenter', () => {
        if (this.closeTimer) {
          clearTimeout(this.closeTimer);
          this.closeTimer = null;
        }
      });
      panel.addEventListener('pointerleave', () => {
        if (!this.mq.matches) return;
        this.scheduleClose();
      });
    });

    this.onKeyDown = (event) => {
      if (event.key === 'Escape') this.close();
    };
    this.onClick = (event) => {
      if (!this.activePanel) return;
      if (this.activePanel.contains(event.target)) return;
      if (this.activeTrigger && this.activeTrigger.contains(event.target)) {
        return;
      }
      this.close();
    };
    this.onFocusIn = (event) => {
      if (!this.activePanel || !this.mq.matches) return;
      if (this.activePanel.contains(event.target)) return;
      if (this.activeTrigger && this.activeTrigger.contains(event.target)) {
        return;
      }
      this.close();
    };
    this.onMqChange = () => this.close();

    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('click', this.onClick);
    document.addEventListener('focusin', this.onFocusIn);
    if (typeof this.mq.addEventListener === 'function') {
      this.mq.addEventListener('change', this.onMqChange);
    }
  }

  destroy() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('click', this.onClick);
    document.removeEventListener('focusin', this.onFocusIn);
    if (typeof this.mq.removeEventListener === 'function') {
      this.mq.removeEventListener('change', this.onMqChange);
    }
    if (this.activePanel) this.close();
  }
}

let submenuPanelsInstance = null;
let panelsWatchTimer = null;

function bootSubmenuPanels() {
  if (submenuPanelsInstance) submenuPanelsInstance.destroy();
  submenuPanelsInstance = new SubmenuPanels();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootSubmenuPanels);
} else {
  bootSubmenuPanels();
}

/* Horizon hydrates the header through the Section Rendering API AFTER
   DOMContentLoaded, so the panels land inside [data-submenu-panels] only after
   this module has already run (and found nothing). Watch for that injection
   and boot once panels exist but no trigger has been attached yet. The
   trigger-presence check keeps this from re-booting after every open/close. */
const panelsWatch = new MutationObserver(() => {
  if (panelsWatchTimer) clearTimeout(panelsWatchTimer);
  panelsWatchTimer = setTimeout(() => {
    const drawer = document.querySelector('.header__drawer');
    if (
      document.querySelector('.submenu-panel') &&
      drawer &&
      !drawer.querySelector('.submenu-panel__trigger')
    ) {
      bootSubmenuPanels();
    }
  }, 150);
});
panelsWatch.observe(document.documentElement, { childList: true, subtree: true });

/* Theme editor: the header section re-renders on setting changes — re-bind so
   the desktop hover and the mobile triggers keep working without a reload. */
document.addEventListener('shopify:section:load', (event) => {
  if (event.target && event.target.querySelector('[data-submenu-panels]')) {
    bootSubmenuPanels();
  }
});
